import { IBulkEditResponse, IBulkManageResponse, ILicenseLink, ILicenseNote, ILicenseUsage, ILicenseV2, IOrgOption } from "../../common/models/license.interface";
import { createDefaultLoadable, ILoadableArray, ILoadableWrapper, Loadable } from "../../common/models/loadable";

export interface ILicenseUsageEntry extends Loadable {
    items: ILicenseUsage[];
}

export interface ILicenseUsageState {
    byLicenseId: Partial<Record<string, ILicenseUsageEntry>>;
}

export interface ILicenseNotesState extends Loadable {
    licenseId: string | null;
    items: ILicenseNote[];
}

export interface IChildOrganisationsState extends Loadable {
    items: IOrgOption[];
}

export interface IBulkEditState extends Loadable {
    response: IBulkEditResponse | null;
}

export interface ISubscriptionsPlusState {
    licenses: ILoadableArray<ILoadableWrapper<ILicenseV2>>;
    selectedLicense: ILoadableWrapper<ILicenseV2> | null;
    includeChildren: boolean;
    distribution: Loadable;
    transition: Loadable;
    bulkEdit: IBulkEditState;
    usage: ILicenseUsageState;
    licenseLinks: ILicenseLinksState;
    globalLinks: IGlobalLinksState;
    linksOverview: ILinksOverviewState;
    linkManagement: ILinkManagementState;
    linkEntity: ILinkEntityState;
    childOrganisations: IChildOrganisationsState;
    notes: ILicenseNotesState;
    noteCreate: Loadable;
    noteDelete: Loadable;
}

export interface ILicenseLinksState extends Loadable {
    linksByLicense: Map<string, Set<string>>;
}

/**
 * Read-only per-license links for display surfaces (license list column, linked-entities
 * dialog). Kept separate from `licenseLinks`/`globalLinks` because those belong to the
 * manage-links editor, which resets them via `clearLinkManagement` on every dialog open
 * and pre-checks cameras from whatever they contain. Full `ILicenseLink[]` is stored
 * (not just entity ids) so `entityType` survives for name resolution.
 */
export interface ILinksOverviewState extends Loadable {
    linksByLicense: Map<string, ILicenseLink[]>;
}

export interface IGlobalLinksState extends Loadable {
    linkedCameraIds: Set<string>;
    cameraToLicense: Map<string, string>;
}

export interface ILinkManagementState extends Loadable {
    response: IBulkManageResponse | null;
}

export interface ILinkEntityState extends Loadable {
    currentlyLinkedOrgId: string | null;
    orgOptions: IOrgOption[];
}

export const initialSubscriptionsPlusState: ISubscriptionsPlusState = {
    licenses: {
        ...createDefaultLoadable(),
        items: []
    },
    selectedLicense: null,
    includeChildren: false,
    distribution: createDefaultLoadable(),
    transition: createDefaultLoadable(),
    bulkEdit: {
        ...createDefaultLoadable(),
        response: null,
    },
    usage: {
        byLicenseId: {},
    },
    licenseLinks: {
        ...createDefaultLoadable(),
        linksByLicense: new Map(),
    },
    globalLinks: {
        ...createDefaultLoadable(),
        linkedCameraIds: new Set(),
        cameraToLicense: new Map(),
    },
    linksOverview: {
        ...createDefaultLoadable(),
        linksByLicense: new Map(),
    },
    linkManagement: {
        ...createDefaultLoadable(),
        response: null,
    },
    linkEntity: {
        ...createDefaultLoadable(),
        currentlyLinkedOrgId: null,
        orgOptions: [],
    },
    childOrganisations: {
        ...createDefaultLoadable(),
        items: [],
    },
    notes: {
        ...createDefaultLoadable(),
        licenseId: null,
        items: [],
    },
    noteCreate: createDefaultLoadable(),
    noteDelete: createDefaultLoadable(),
};
